// 段落ごとの印の上限と、題の語の印を外す rehype プラグイン。
//
// 用語集 (glossary-term) と語の前の印 (span.ilogo-w) を置いたあとの木に当てる。
// - 1 つの段落 (BLOCK_TAGS) の印を perBlock 個までにする。用語の点線と、同じ語に付いた印は合わせて 1 つと数える
// - 用語の点線は外さない (rehypeGlossary が段落の上限を守って付けている)。残りの枠に、点線の無い語の印を前から入れる
// - title に出ている語 (教材の題のスクラムなど) の印は、枠と関係なく外す
// 外すときは印の svg だけを除き、語の字 (と用語の要素) はそのまま残す

/** 印を数える段落の単位。入れ子 (li の中の p) は内側の要素が自分の分を数える */
export const BLOCK_TAGS = new Set(["p", "li", "dd", "dt", "td", "th", "figcaption", "summary"]);
// 段落の外の入れ物。中の段落はそれぞれが自分で数える
const CONTAINER_TAGS = new Set(["ul", "ol", "table", "blockquote", "div", "section", "details", "pre"]);

type Icon = { el: any; parent: any; word: string; hasTerm: boolean };

const ASCII_WORD = /[A-Za-z0-9_]/;

/**
 * 題に語が出ているか。英数字の語は前後が英数字でないときだけ当たる (PostgreSQL の中の OS は当たらない)。
 * 和文の語はそのまま含まれていれば当たる。1 字の語は当てない
 */
export function titleHasWord(title: string | null | undefined, word: string): boolean {
  const t = (title ?? "").toLowerCase();
  const w = word.trim().toLowerCase();
  if (!t || w.length < 2) return false;
  for (let i = t.indexOf(w); i >= 0; i = t.indexOf(w, i + 1)) {
    const before = t[i - 1] ?? "";
    const after = t[i + w.length] ?? "";
    const edgeOk = (inner: string, outer: string) => !(ASCII_WORD.test(inner) && ASCII_WORD.test(outer));
    if (edgeOk(w[0], before) && edgeOk(w[w.length - 1], after)) return true;
  }
  return false;
}

const isIconWord = (n: any) =>
  n?.type === "element" && n.tagName === "span" && [n.properties?.className ?? []].flat().includes("ilogo-w");

function textOf(n: any): string {
  if (n.type === "text") return String(n.value ?? "");
  if (n.type === "element" && n.tagName === "svg") return "";
  return (n.children ?? []).map(textOf).join("");
}

function hasTerm(n: any): boolean {
  if (n.type === "element" && n.tagName === "glossary-term") return true;
  return (n.children ?? []).some(hasTerm);
}

function strip(icon: Icon) {
  const i = icon.parent.children.indexOf(icon.el);
  if (i < 0) return;
  const kids = (icon.el.children ?? []).filter((c: any) => !(c.type === "element" && c.tagName === "svg"));
  icon.parent.children.splice(i, 1, ...kids);
}

export function rehypeLimitInlineMarks(options: { perBlock?: number; title?: string } = {}) {
  const perBlock = options.perBlock ?? Infinity;
  const inTitle = (word: string) => titleHasWord(options.title, word);

  return (tree: any) => {
    const limitBlock = (block: any) => {
      let terms = 0;
      const icons: Icon[] = [];
      const collect = (node: any) => {
        for (const c of node.children ?? []) {
          if (c.type !== "element") continue;
          const tag = String(c.tagName).toLowerCase();
          if (BLOCK_TAGS.has(tag) || CONTAINER_TAGS.has(tag)) continue;
          if (isIconWord(c)) {
            const withTerm = hasTerm(c);
            if (withTerm) terms++;
            icons.push({ el: c, parent: node, word: textOf(c), hasTerm: withTerm });
            continue;
          }
          if (tag === "glossary-term") {
            terms++;
            continue;
          }
          collect(c);
        }
      };
      collect(block);
      let room = perBlock - terms;
      for (const icon of icons) {
        if (inTitle(icon.word)) strip(icon);
        else if (icon.hasTerm) continue;
        else if (room > 0) room--;
        else strip(icon);
      }
    };

    const walk = (node: any) => {
      for (const c of node.children ?? []) {
        if (c.type !== "element") continue;
        if (BLOCK_TAGS.has(String(c.tagName).toLowerCase())) limitBlock(c);
        walk(c);
      }
    };
    walk(tree);
  };
}
